import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Bell,
  Send,
  Loader2,
  Users,
  Crown,
  Heart,
  TrendingUp,
  Clock,
  MessageSquare,
  ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const SEGMENTS = [
  { id: "all", label: "Toutes les clientes", icon: Users, description: "Envoyer à toutes les clientes avec notifications activées" },
  { id: "vip", label: "Clientes VIP", icon: Crown, description: "Clientes ayant passé 5+ commandes" },
  { id: "active", label: "Clientes actives", icon: TrendingUp, description: "Connectées dans les 30 derniers jours" },
  { id: "with_favorites", label: "Avec favoris", icon: Heart, description: "Clientes ayant des articles en favoris" },
];

export default function Notifications() {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Send dialog
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    body: "",
    url: "",
    segment: "all"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/notifications/stats`, getAuthHeaders()),
        axios.get(`${API}/admin/notifications/history`, getAuthHeaders())
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const sendNotification = async () => {
    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error("Veuillez remplir le titre et le message");
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/admin/notifications/send`,
        formData,
        getAuthHeaders()
      );
      
      toast.success(response.data.message);
      setSendOpen(false);
      setFormData({ title: "", body: "", url: "", segment: "all" });
      fetchData(); // Refresh stats and history
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Notifications</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Envoyez des notifications push à vos clientes
          </p>
        </div>
        <Button onClick={() => setSendOpen(true)} data-testid="send-notification-btn">
          <Send className="w-4 h-4 mr-2" />
          Nouvelle campagne
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_customers}</p>
                  <p className="text-xs text-muted-foreground">Total clientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.push_enabled}</p>
                  <p className="text-xs text-muted-foreground">Notifs activées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-500/10 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.push_disabled}</p>
                  <p className="text-xs text-muted-foreground">Non activées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Send className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.notifications_sent}</p>
                  <p className="text-xs text-muted-foreground">Envoyées</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Segment Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Segments disponibles</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {SEGMENTS.map(segment => (
            <Card 
              key={segment.id}
              className="cursor-pointer hover:shadow-md transition-all"
              onClick={() => {
                setFormData(prev => ({ ...prev, segment: segment.id }));
                setSendOpen(true);
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <segment.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{segment.label}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{segment.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* History */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Historique des envois</h2>
        {history.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Bell className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Aucune notification envoyée</p>
              <p className="text-sm text-muted-foreground mt-1">
                Créez votre première campagne pour communiquer avec vos clientes
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {history.map(notif => (
              <Card key={notif.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{notif.title}</span>
                        <Badge variant="outline" className="text-xs">
                          {SEGMENTS.find(s => s.id === notif.segment)?.label || notif.segment}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">{notif.body}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-medium">{notif.recipients_count} destinataires</p>
                      <p className="text-xs text-muted-foreground">{formatDate(notif.created_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Send Dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle campagne</DialogTitle>
            <DialogDescription>
              Envoyez une notification push à vos clientes
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="segment">Destinataires</Label>
              <Select
                value={formData.segment}
                onValueChange={(value) => setFormData(prev => ({ ...prev, segment: value }))}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Sélectionner un segment" />
                </SelectTrigger>
                <SelectContent>
                  {SEGMENTS.map(segment => (
                    <SelectItem key={segment.id} value={segment.id}>
                      <div className="flex items-center gap-2">
                        <segment.icon className="w-4 h-4" />
                        {segment.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="title">Titre *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ex: Nouvelle collection disponible !"
                className="mt-1.5"
                maxLength={50}
              />
              <p className="text-xs text-muted-foreground mt-1">{formData.title.length}/50 caractères</p>
            </div>
            
            <div>
              <Label htmlFor="body">Message *</Label>
              <Textarea
                id="body"
                value={formData.body}
                onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Ex: Découvrez nos dernières pièces de la saison..."
                rows={3}
                className="mt-1.5"
                maxLength={200}
              />
              <p className="text-xs text-muted-foreground mt-1">{formData.body.length}/200 caractères</p>
            </div>
            
            <div>
              <Label htmlFor="url">Lien (optionnel)</Label>
              <Input
                id="url"
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                placeholder="Ex: /vitrine/xyz/product/abc"
                className="mt-1.5"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Page à ouvrir quand la cliente clique
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={sendNotification}
              disabled={sending || !formData.title.trim() || !formData.body.trim()}
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
